<script setup lang="ts">
  import { useI18n } from 'vue-i18n'

  import { ArrowLeftIcon, Bell01Icon, DoubleCheckIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPBadge from '@ui/components/CPBadge.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPDrawer from '@ui/components/CPDrawer.vue'
  import CPText from '@ui/components/CPText.vue'
  import CPToggle from '@ui/components/CPToggle.vue'

  import { useNotificationDrawer } from '../composables/useNotificationDrawer'
  import NotificationList from './NotificationList.vue'
  import { notificationWebProps } from './notificationWeb.shared'

  const props = defineProps(notificationWebProps)

  const emit = defineEmits<{
    (e: 'update:notificationListIsOpen'): void
  }>()

  const {
    closeNotificationList,
    currentNotifications,
    handleMarkAllRead,
    handleNotificationItemClick,
    isCurrentQueryFetchingNextPage,
    isCurrentQueryLoading,
    isMarkAllReadDisabled,
    loadMoreTrigger,
    notificationCounterOffset,
    notificationVisibility,
    setNotificationVisibility,
    toggleNotificationList,
    unReadNotificationCount,
  } = useNotificationDrawer(props, emit)

  const { t } = useI18n()
</script>

<template>
  <div class="z-20 font-semibold text-clientportal-sidebar-font-secondary">
    <div
      :id="createElementId('navbar', 'notification', 'bell-icon')"
      role="button"
      tabindex="0"
      class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg transition-colors duration-150 hover:bg-hover"
      :class="[!profileCardIsOpen && !appLauncherIsOpen && notificationListIsOpen ? 'bg-hover' : '']"
      @click="toggleNotificationList"
      @keydown.enter.prevent="toggleNotificationList"
      @keydown.space.prevent="toggleNotificationList"
    >
      <CPBadge
        :id="createElementId('navbar', 'notification', 'badge')"
        class="notification-bell-badge"
        :offset="notificationCounterOffset"
        :show="unReadNotificationCount > 0"
        :value="unReadNotificationCount > 9 ? '9+' : unReadNotificationCount"
      >
        <Bell01Icon class="h-5 w-5 text-600" />
      </CPBadge>
    </div>

    <CPDrawer
      :id="createElementId('navbar', 'notification', 'mobile-drawer')"
      :show="notificationListIsOpen"
      placement="right"
      width="100vw"
      :show-mask="false"
      :mask-closable="false"
      :closable="false"
      content-class="notification-mobile-drawer"
      body-class="!p-0"
      body-content-class="notification-mobile-drawer-body"
      @esc="closeNotificationList"
    >
      <div class="notification-mobile-shell flex h-full flex-col">
        <div class="notification-mobile-scroll flex-1 overflow-y-auto px-2 pb-4">
          <div
            :id="createElementId('navbar', 'notification', 'mobile-header')"
            class="notification-mobile-header sticky top-0 z-10 -mx-2 flex items-center justify-between gap-3 border-b border-default px-4 py-3"
          >
            <div class="flex min-w-0 items-center gap-3">
              <CPButton
                :id="createElementId('navbar', 'notification', 'back-btn')"
                type="text"
                class="!h-8 !w-8 !min-w-8 !rounded-full !p-0"
                @click="closeNotificationList"
              >
                <ArrowLeftIcon class="h-5 w-5 text-body" />
              </CPButton>
              <CPText :id="createElementId('navbar', 'notification', 'mobile-title')" size="2xl" weight="semibold" class="text-body">
                {{ t('notifications.title') }}
              </CPText>
            </div>
            <CPButton
              :id="createElementId('navbar', 'notification', 'mark-all-read-btn')"
              type="text"
              size="sm"
              :disabled="isMarkAllReadDisabled"
              :class="['h-auto px-0 py-0', isMarkAllReadDisabled ? 'opacity-50 cursor-not-allowed' : '']"
              @click="handleMarkAllRead"
            >
              <DoubleCheckIcon class="mr-1.5 h-4 w-4 text-600" />
              <CPText size="sm" weight="semibold" class="text-700">
                {{ t('notifications.markAllRead') }}
              </CPText>
            </CPButton>
          </div>

          <div class="notification-mobile-filter border-b border-default px-2 py-3.5">
            <div :id="createElementId('navbar', 'notification', 'unread-filter-row')" class="flex items-center gap-2">
              <CPToggle
                :id="createElementId('navbar', 'notification', 'toggle-btn')"
                size="sm"
                :value="notificationVisibility === 'unread'"
                color="blue"
                @update:value="(value: boolean) => setNotificationVisibility(value ? 'unread' : 'all')"
              />
              <CPText :id="createElementId('navbar', 'notification', 'show-unread-label')" size="sm" class="text-body">
                {{ t('notifications.showOnlyUnread') }}
              </CPText>
            </div>
          </div>

          <NotificationList
            :notifications="currentNotifications"
            :is-loading="isCurrentQueryLoading"
            :is-fetching-next-page="isCurrentQueryFetchingNextPage"
            @itemClicked="handleNotificationItemClick"
          />
          <div
            :id="createElementId('navbar', 'notification', 'load-more-trigger')"
            ref="loadMoreTrigger"
            class="h-4"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    </CPDrawer>
  </div>
</template>

<style scoped>
  .notification-bell-badge :deep(.hr-badge-sup) {
    background-color: #f04438;
  }

  .notification-bell-badge :deep(.hr-badge-sup .hr-base-slot-machine) {
    color: #ffffff !important;
  }

  :deep(.notification-mobile-drawer) {
    width: 100vw !important;
    max-width: 100vw !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  :deep(.notification-mobile-drawer .hr-drawer-body-content) {
    height: 100%;
    background-color: var(--cp-background-500);
  }

  :deep(.notification-mobile-drawer-body) {
    height: 100%;
    background-color: var(--cp-background-500);
  }

  .notification-mobile-shell,
  .notification-mobile-scroll,
  .notification-mobile-header,
  .notification-mobile-filter {
    background-color: var(--cp-background-500);
  }

  .notification-mobile-header {
    box-shadow: 0 6px 18px color-mix(in oklab, var(--cp-font-900) 4%, transparent);
  }
</style>
